import { useEffect, useRef, useState } from 'react';
import { api } from './api';
import { accountStatus } from './balance';
import { accountHistory, isProp, payoutsOf } from './business';
import { dayKey, formatMoney, netPnl, usd, tradingDayKey } from './pnl';
import { currentLocale, translateNow as tn } from './langState';
import { useI18n } from './i18n';

export const AUTO_TYPES = [
  { id: 'account_target', label: "An account's profit target", needsAccount: true },
  { id: 'rules_streak', label: 'Days in a row following all my rules', unit: 'days' },
  { id: 'journal_streak', label: 'Days in a row journaling', unit: 'days' },
  { id: 'win_rate_month', label: 'Win rate', unit: '%' },
  { id: 'net_pnl_month', label: 'Net P&L', unit: '$' },
  // My Trading Journey goals: counted from the goal's start date over every account, past
  // attempts included.
  { id: 'pass_evaluation', label: 'Evaluations passed', unit: 'count' },
  { id: 'account_lifespan', label: 'A prop account that lasts', unit: 'calendar days' },
  { id: 'payouts_total', label: 'Payouts adding up to', unit: '$' },
];

// --- The goal's own dates ----------------------------------------------------
// A tracked goal only counts trades from its start date (auto.from) to its target date,
// so history from before the goal was set can't complete it. Goals saved before start
// dates existed count from the trading day they were created.

const addDays = (key, n) => {
  const d = new Date(`${key}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const isWeekend = (key) => [0, 6].includes(new Date(`${key}T12:00:00Z`).getUTCDay());

// The first day a new goal counts: today's session, unless it's the weekend or there are
// already trades today, then the next weekday (a goal set on Sunday starts Monday).
export function defaultGoalStart(trades = [], now = new Date()) {
  let d = tradingDayKey(now);
  if (trades.some((t) => tradingDayKey(t.date) === d)) d = addDays(d, 1);
  while (isWeekend(d)) d = addDays(d, 1);
  return d;
}

export function goalStart(goal) {
  if (goal.auto && goal.auto.from) return goal.auto.from;
  return goal.createdAt ? tradingDayKey(goal.createdAt) : null;
}

function goalWindow(goal) {
  const from = goalStart(goal);
  const to = goal.targetDate && (!from || goal.targetDate >= from) ? goal.targetDate : null;
  return { from, to, has: (day) => (!from || day >= from) && (!to || day <= to) };
}

const shortDate = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });

// A win rate needs enough trades to mean something: the goal's period has to be over,
// or, without a target date, at least this many trades.
const WIN_RATE_MIN_TRADES = 10;

const STATUS_LABEL = { not_started: 'Not started', in_progress: 'In progress', done: 'Done' };

// Days with at least one trade, newest first, leaving out today until its
// reflection has been started (so a streak isn't broken before the day is over).
function tradingDays(trades, notes, inWindow = () => true) {
  const today = dayKey(new Date());
  const started = (n) => isJournaled(n) || Boolean(n && n.rulesFollowed && n.rulesFollowed.length);
  const days = [...new Set(trades.map((t) => tradingDayKey(t.date)))].filter(inWindow).sort().reverse();
  return days.filter((d) => d !== today || started(notes[today]));
}

function isJournaled(n) {
  return Boolean(n && (n.score || (n.review && n.review.trim()) || (n.eveningDone && n.eveningDone.length)));
}

function followedAll(n, rules) {
  return Boolean(n && rules.length && rules.every((r) => (n.rulesFollowed || []).includes(r)));
}

function streak(days, test) {
  let count = 0;
  for (const d of days) {
    if (!test(d)) break;
    count += 1;
  }
  return count;
}

// Progress for an auto-tracked goal from the trader's data.
export function autoProgress(goal, { accounts, trades, notes, rules }) {
  const a = goal.auto;
  if (!a) return null;
  const win = goalWindow(goal);
  const inWindow = (t) => win.has(tradingDayKey(t.date));
  const notStarted = win.from && win.from > tradingDayKey(new Date());
  const startsLabel = () => tn('Starts {date}', { date: shortDate(win.from) });
  if (a.type === 'account_target') {
    const account = accounts.find((x) => x._id === a.accountId);
    if (!account) return { progress: 0, label: tn('Account not found'), done: false };
    const accountTrades = trades.filter((t) => t.accountId === account._id);
    const s = accountStatus(account, accountTrades);
    if (!s || s.target === undefined) {
      // No profit target on the account (e.g. a personal account): the goal's own target,
      // reached by the account's net P&L across all its trades.
      const target = a.target > 0 ? a.target : account.profitTarget;
      if (!(target > 0)) return { progress: 0, label: tn('Set a profit target on {name}', { name: account.name }), done: false };
      if (notStarted) return { progress: 0, done: false, label: startsLabel() };
      const net = accountTrades.filter(inWindow).reduce((sum, t) => sum + netPnl(t), 0);
      const done = net >= target;
      return {
        progress: Math.max(0, Math.min(1, net / target)),
        done,
        label: done ? tn('Target reached on {name}', { name: account.name })
          : tn('{amount} of {target} on {name}', { amount: formatMoney(net), target: usd.format(target), name: account.name }),
      };
    }
    const done = s.toPass <= 0;
    return {
      progress: s.progress,
      done,
      label: done ? tn('Target reached on {name}', { name: account.name })
        : tn('{amount} of {target} on {name}', { amount: usd.format(Math.max(0, s.change)), target: usd.format(s.target - s.start), name: account.name }),
    };
  }
  if (notStarted) return { progress: 0, done: false, label: startsLabel() };
  if (a.type === 'pass_evaluation' || a.type === 'account_lifespan') {
    const history = accountHistory(accounts, trades).filter((h) => !win.from || h.start >= win.from);
    if (a.type === 'pass_evaluation') {
      const n = history.filter((h) => h.type === 'Evaluation' && h.outcome === 'passed' && h.end && win.has(h.end)).length;
      return {
        progress: Math.min(1, n / a.target), done: n >= a.target,
        label: tn(a.target === 1 ? '{n} of {target} evaluation passed' : '{n} of {target} evaluations passed', { n, target: a.target }),
      };
    }
    const longest = history.filter((h) => isProp(h.account)).reduce((m, h) => Math.max(m, h.days), 0);
    return {
      progress: Math.min(1, longest / a.target), done: longest >= a.target,
      label: tn('Longest so far: {n} of {target} days', { n: longest, target: a.target }),
    };
  }
  if (a.type === 'payouts_total') {
    const total = payoutsOf(accounts).filter((p) => win.has(p.date)).reduce((s, p) => s + p.amount, 0);
    return {
      progress: Math.min(1, total / a.target), done: total >= a.target,
      label: tn('{amount} of {target} in payouts', { amount: usd.format(total), target: usd.format(a.target) }),
    };
  }
  if (a.type === 'rules_streak' || a.type === 'journal_streak') {
    const days = tradingDays(trades, notes, win.has);
    const count = streak(days, (d) => (a.type === 'rules_streak' ? followedAll(notes[d], rules) : isJournaled(notes[d])));
    return {
      progress: Math.min(1, count / a.target),
      done: count >= a.target,
      label: count >= a.target
        ? tn('{n} trading days in a row (goal: {target})', { n: count, target: a.target })
        : tn('{n} of {target} trading days in a row', { n: count, target: a.target }),
    };
  }
  const period = trades.filter(inWindow);
  const since = win.from ? tn('since {date}', { date: shortDate(win.from) }) : '';
  if (a.type === 'win_rate_month') {
    if (!period.length) return { progress: 0, done: false, label: win.from ? tn('No trades since {date} yet', { date: shortDate(win.from) }) : tn('No trades yet') };
    const rate = (period.filter((t) => netPnl(t) > 0).length / period.length) * 100;
    const over = win.to ? win.to < tradingDayKey(new Date()) : period.length >= WIN_RATE_MIN_TRADES;
    return {
      progress: Math.min(1, rate / a.target),
      done: over && rate >= a.target,
      label: tn('{rate}% {since} (target {target}%) over {n} trades', { rate: Math.round(rate), since, target: a.target, n: period.length }).replace(/\s+/g, ' '),
    };
  }
  const net = period.reduce((s, t) => s + netPnl(t), 0);
  return {
    progress: Math.max(0, Math.min(1, net / a.target)),
    done: net >= a.target,
    label: tn('{amount} of {target} {since}', { amount: formatMoney(net), target: usd.format(a.target), since }).trim(),
  };
}

// Progress (0–1) and done-ness for any goal, auto or manual.
export function goalProgress(goal, data) {
  // Once reached, a goal stays reached (a streak that breaks later doesn't undo it).
  if (goal.doneAt) return { progress: 1, done: true, label: tn('Reached {date}', { date: shortDate(goal.doneAt) }) };
  const auto = autoProgress(goal, data);
  if (auto) return auto;
  if (goal.status === 'done') return { progress: 1, done: true };
  if (goal.steps && goal.steps.length) {
    const done = goal.steps.filter((s) => s.done).length;
    return { progress: done / goal.steps.length, done: false, label: tn('{done} of {total} steps done', { done, total: goal.steps.length }) };
  }
  if (goal.progress != null) return { progress: goal.progress / 100, done: false, label: `${goal.progress}%` };
  return { progress: 0, done: false };
}

// A goal's reminder is due on `date` (the trader's own day): every day, or on its weekday.
export function goalReminderDue(goal, date = new Date()) {
  const r = goal.reminder;
  return Boolean(r && goal.status !== 'done' && (r.frequency === 'daily' || (r.frequency === 'weekly' && r.weekday === date.getDay())));
}

// "Every day", "Every Monday", plus "· email" when it's emailed too.
export function reminderLabel(reminder) {
  if (!reminder) return '';
  const when = reminder.frequency === 'daily'
    ? tn('Every day')
    : tn('Every {day}', { day: weekdayName(reminder.weekday) });
  if (!reminder.email) return when;
  return reminder.time ? `${when} · ${tn('email at {time}', { time: timeLabel(reminder.time) })}` : `${when} · ${tn('email')}`;
}

// Jan 4, 2026 was a Sunday, so day 0 is Sunday like Date.getDay().
function weekdayName(i) {
  return new Date(2026, 0, 4 + i).toLocaleDateString(currentLocale(), { weekday: 'long' });
}

// "08:00" as "8:00 AM" (or "8:00" in Spanish).
function timeLabel(hhmm) {
  const [h, m] = String(hhmm || '08:00').split(':').map(Number);
  return new Date(2026, 0, 5, h, m).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });
}

function daysLeft(targetDate) {
  if (!targetDate) return null;
  const today = new Date(`${dayKey(new Date())}T12:00:00`);
  return Math.round((new Date(`${targetDate}T12:00:00`) - today) / 86400000);
}

export function dueLabel(goal, done) {
  const left = daysLeft(goal.targetDate);
  if (left === null) return null;
  const date = new Date(`${goal.targetDate}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
  if (done) return tn('Target {date}', { date });
  if (left < 0) return tn('Overdue · {date}', { date });
  if (left === 0) return tn('Due today');
  return tn(left === 1 ? '{n} day left · {date}' : '{n} days left · {date}', { n: left, date });
}

const emptyGoal = { title: '', horizon: 'short', kind: 'process', targetDate: '', tracking: 'manual', autoType: 'rules_streak', accountId: '', target: '', from: '', notes: '', remind: 'off', remindDay: 1, remindEmail: false, remindTime: '' };

function GoalEditor({ goal, accounts, trades = [], user, onSaved, onDeleted, onClose, onSessionExpired }) {
  const { t } = useI18n();
  const defaultTime = (user && user.emailPrefs && user.emailPrefs.reminderTime) || '08:00';
  const [form, setForm] = useState(() => (goal ? {
    title: goal.title,
    horizon: goal.horizon,
    kind: goal.kind,
    targetDate: goal.targetDate || '',
    tracking: goal.auto ? 'auto' : 'manual',
    autoType: goal.auto ? goal.auto.type : 'rules_streak',
    accountId: goal.auto && goal.auto.accountId ? goal.auto.accountId : '',
    target: goal.auto && goal.auto.target ? String(goal.auto.target) : '',
    from: goalStart(goal) || defaultGoalStart(trades),
    notes: goal.notes || '',
    remind: goal.reminder ? goal.reminder.frequency : 'off',
    remindDay: goal.reminder && goal.reminder.weekday != null ? goal.reminder.weekday : 1,
    remindEmail: Boolean(goal.reminder && goal.reminder.email),
    remindTime: (goal.reminder && goal.reminder.time) || defaultTime,
  } : { ...emptyGoal, remindTime: defaultTime, from: defaultGoalStart(trades), accountId: accounts.find((a) => !a.archived)?._id || '' }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dialog = useRef(null);
  const autoType = AUTO_TYPES.find((x) => x.id === form.autoType);
  const chosenAccount = accounts.find((a) => a._id === form.accountId);
  // An account with its own profit target uses it; any other account needs one here.
  const accountHasTarget = Boolean(chosenAccount && chosenAccount.profitTarget > 0);
  // A prop account's own target is the account's whole evaluation, so it has no start date.
  const usesStart = !(autoType.needsAccount && accountHasTarget);

  useEffect(() => {
    dialog.current?.focus();
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function set(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    const body = {
      title: form.title,
      horizon: form.horizon,
      kind: form.kind,
      targetDate: form.targetDate || null,
      notes: form.notes,
      reminder: form.remind === 'off' ? null
        : {
          frequency: form.remind,
          email: form.remindEmail,
          ...(form.remindEmail ? { time: form.remindTime } : {}),
          ...(form.remind === 'weekly' ? { weekday: Number(form.remindDay) } : {}),
        },
      auto: form.tracking === 'auto'
        ? {
          type: form.autoType,
          ...(autoType.needsAccount ? { accountId: form.accountId, ...(accountHasTarget ? {} : { target: Number(form.target) }) } : { target: Number(form.target) }),
          ...(usesStart && form.from ? { from: form.from } : {}),
        }
        : null,
    };
    try {
      const saved = goal
        ? await api(`/goals/${goal._id}`, { method: 'PATCH', body })
        : await api('/goals', { method: 'POST', body: { ...body, status: 'in_progress' } });
      onSaved(saved);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      setSaving(false);
    }
  }

  async function remove() {
    setSaving(true);
    try {
      await api(`/goals/${goal._id}`, { method: 'DELETE' });
      onDeleted(goal._id);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form ref={dialog} tabIndex={-1} className="modal" onSubmit={save} role="dialog" aria-modal="true" aria-labelledby="goal-title">
        <div className="modal-header">
          <h2 id="goal-title">{goal ? t('Edit goal') : t('New goal')}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        <label>
          {t('Goal')}
          <input name="title" value={form.title} onChange={set} maxLength={200} required
            placeholder={t('e.g. Pass my Topstep 50K, or Follow all my rules for 10 days')} />
        </label>
        <div className="fields">
          <label>
            {t('Timeframe')}
            <select name="horizon" value={form.horizon} onChange={set}>
              <option value="short">{t('Short term (this week or month)')}</option>
              <option value="long">{t('Long term (6 months or more)')}</option>
            </select>
          </label>
          <label>
            {t('Type')}
            <select name="kind" value={form.kind} onChange={set}>
              <option value="process">{t('Process goal (a habit I control)')}</option>
              <option value="outcome">{t('Trading goal (a result)')}</option>
              <option value="personal">{t('Personal goal (health, family, life outside trading)')}</option>
            </select>
          </label>
          <label>
            {t('Target date')}
            <input name="targetDate" type="date" value={form.targetDate} onChange={set} />
          </label>
        </div>

        <div className="modal-section">
          <span className="modal-section-label">{t('How is it tracked?')}</span>
          <div className="impact-buttons tracking-toggle">
            <button type="button" className={`impact-button${form.tracking === 'manual' ? ' selected tracking-on' : ''}`}
              onClick={() => setForm({ ...form, tracking: 'manual' })}>{t("I'll update it myself")}</button>
            <button type="button" className={`impact-button${form.tracking === 'auto' ? ' selected tracking-on' : ''}`}
              onClick={() => setForm({ ...form, tracking: 'auto' })}>{t('Track it from my data')}</button>
          </div>
          {form.tracking === 'auto' && (
            <div className="fields">
              <label>
                {t('Track')}
                <select name="autoType" value={form.autoType} onChange={set}>
                  {AUTO_TYPES.map((x) => <option key={x.id} value={x.id}>{t(x.label)}</option>)}
                </select>
              </label>
              {autoType.needsAccount ? (
                <>
                  <label>
                    {t('Account')}
                    <select name="accountId" value={form.accountId} onChange={set} required>
                      <option value="" disabled>{t('Choose an account')}</option>
                      {accounts.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
                    </select>
                  </label>
                  {chosenAccount && !accountHasTarget && (
                    <label>
                      {t('Profit target (dollars)')}
                      <input name="target" type="number" min="1" step="any" value={form.target} onChange={set} required placeholder={t('e.g. 3000')} />
                    </label>
                  )}
                </>
              ) : (
                <label>
                  {autoType.unit === '$' ? t('Target (dollars)') : autoType.unit === '%' ? t('Target (percent)') : autoType.unit === 'count' ? t('Target (how many)') : autoType.unit === 'calendar days' ? t('Target (days)') : t('Target (trading days)')}
                  <input name="target" type="number" min="1" step={autoType.unit === '$' ? 'any' : '1'} value={form.target}
                    onChange={set} required placeholder={autoType.unit === '$' ? t('e.g. 3000') : autoType.unit === '%' ? t('e.g. 55') : autoType.unit === 'count' ? t('e.g. 1') : autoType.unit === 'calendar days' ? t('e.g. 30') : t('e.g. 10')} />
                </label>
              )}
            </div>
          )}
          {form.tracking === 'auto' && usesStart && (
            <div className="fields goal-start">
              <label>
                {t('Start counting from')}
                <input name="from" type="date" value={form.from} onChange={set} required max={form.targetDate || undefined} />
              </label>
              <p className="field-hint">
                {t('Only trades from {from} on count toward this goal{until}, so nothing from before you set it.', {
                  from: form.from ? shortDate(form.from) : '…',
                  until: form.targetDate ? t(' until {date}', { date: shortDate(form.targetDate) }) : '',
                })}
              </p>
            </div>
          )}
          {form.tracking === 'auto' && autoType.needsAccount && chosenAccount && (
            <p className="field-hint">
              {accountHasTarget
                ? t('Uses the profit target saved on {name}: {amount}.', { name: chosenAccount.name, amount: usd.format(chosenAccount.profitTarget) })
                : t('{name} has no profit target of its own, so set one here. Progress is the net P&L (after fees) of the trades in {name} from the start date; deposits and withdrawals don’t count.', { name: chosenAccount.name })}
            </p>
          )}
          {form.tracking === 'manual' && (
            <p className="field-hint">{t("You'll tick off steps and set progress on the goal card.")}</p>
          )}
        </div>

        <div className="modal-section">
          <span className="modal-section-label">{t('Reminder')}</span>
          <div className="impact-buttons tracking-toggle">
            {[['off', t('Off')], ['daily', t('Every day')], ['weekly', t('Every week')]].map(([id, label]) => (
              <button key={id} type="button" className={`impact-button${form.remind === id ? ' selected tracking-on' : ''}`}
                onClick={() => setForm({ ...form, remind: id })}>{label}</button>
            ))}
          </div>
          {form.remind !== 'off' && (
            <div className="goal-reminder-options">
              {form.remind === 'weekly' && (
                <label>
                  {t('On')}
                  <select name="remindDay" value={form.remindDay} onChange={set}>
                    {[1, 2, 3, 4, 5, 6, 0].map((d) => <option key={d} value={d}>{weekdayName(d)}</option>)}
                  </select>
                </label>
              )}
              <div className="goal-reminder-email-row">
                <label className="goal-reminder-email">
                  <input type="checkbox" checked={form.remindEmail} onChange={(e) => setForm({ ...form, remindEmail: e.target.checked })} />
                  {form.remind === 'weekly'
                    ? t('Also email me on {day} at', { day: weekdayName(Number(form.remindDay)) })
                    : t('Also email me every day at')}
                </label>
                {form.remindEmail && (
                  <input type="time" name="remindTime" className="goal-reminder-time" value={form.remindTime} onChange={set} required
                    aria-label={t('Email time')} />
                )}
              </div>
            </div>
          )}
          <p className="field-hint">
            {form.remind === 'off'
              ? t('Turn on a reminder so this goal doesn’t slip out of sight.')
              : t('It shows on your Home page {when}, with its progress and why it matters.', { when: form.remind === 'daily' ? t('every day') : t('every {day}', { day: weekdayName(Number(form.remindDay)) }) })}
            {form.remind !== 'off' && form.remindEmail && form.remindTime && ` ${t('Email at {time}, your time.', { time: timeLabel(form.remindTime) })}`}
          </p>
        </div>

        <label>
          {t('Why this goal matters (optional)')}
          <textarea name="notes" rows="2" value={form.notes} onChange={set} maxLength={2000} />
        </label>

        {error && <p className="error">{error}</p>}
        <div className="modal-actions">
          <button type="submit" disabled={saving || !form.title.trim()}>{saving ? t('Saving…') : goal ? t('Save goal') : t('Add goal')}</button>
          <button type="button" className="secondary" onClick={onClose} disabled={saving}>{t('Cancel')}</button>
          {goal && (
            <div className="modal-delete">
              {confirmDelete ? (
                <>
                  <span>{t('Delete this goal?')}</span>
                  <button type="button" className="danger small" onClick={remove} disabled={saving}>{t('Yes, delete')}</button>
                  <button type="button" className="link-button" onClick={() => setConfirmDelete(false)}>{t('No')}</button>
                </>
              ) : (
                <button type="button" className="link-button danger-link" onClick={() => setConfirmDelete(true)}>{t('Delete goal')}</button>
              )}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

function GoalCard({ goal, data, onChange, onEdit, onSessionExpired }) {
  const { t } = useI18n();
  const [newStep, setNewStep] = useState('');
  const p = goalProgress(goal, data);
  const done = p.done;
  const due = dueLabel(goal, done);

  async function patch(changes) {
    onChange({ ...goal, ...changes });
    try {
      const saved = await api(`/goals/${goal._id}`, { method: 'PATCH', body: changes });
      onChange(saved);
    } catch (err) {
      if (err.status === 401) onSessionExpired();
    }
  }

  function addStep() {
    const text = newStep.trim();
    if (!text) return;
    patch({ steps: [...goal.steps, { text, done: false }], status: goal.status === 'not_started' ? 'in_progress' : goal.status });
    setNewStep('');
  }

  return (
    <li className={`goal-card${done ? ' done' : ''}${goal.kind === 'process' || goal.kind === 'personal' ? ` ${goal.kind}` : ''}`}>
      <div className="goal-head">
        <div className="goal-title-row">
          <span className={`goal-kind ${goal.kind}`}>{goal.kind === 'process' ? t('Process') : goal.kind === 'personal' ? t('Personal') : t('Trading')}</span>
          {goal.auto && <span className="goal-auto">{t('Auto-tracked')}</span>}
          <h3 className="goal-title">{goal.title}</h3>
        </div>
        <button type="button" className="link-button" onClick={onEdit}>{t('Edit')}</button>
      </div>

      <div className="goal-progress">
        <div className="status-bar" role="img" aria-label={t('{pct}% complete', { pct: Math.round(p.progress * 100) })}>
          <span className={`status-bar-fill ${done ? 'target' : 'goal-fill'}`} style={{ width: `${p.progress * 100}%` }} />
        </div>
        <div className="goal-meta">
          <span className={done ? 'gain' : ''}>{done ? `✓ ${t('Achieved')}` : p.label || `${Math.round(p.progress * 100)}%`}</span>
          {due && <span className={daysLeft(goal.targetDate) < 0 && !done ? 'loss' : ''}>{due}</span>}
          {goal.reminder && !done && <span className="goal-reminder-chip">{t('Reminder: {when}', { when: reminderLabel(goal.reminder) })}</span>}
        </div>
      </div>

      {!goal.auto && (
        <>
          {goal.steps.length > 0 && (
            <ul className="checklist goal-steps">
              {goal.steps.map((s, i) => (
                // Steps have no ids; position is their identity.
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>
                  <label className={`check-item${s.done ? ' checked' : ''}`}>
                    <input type="checkbox" checked={s.done}
                      onChange={() => patch({ steps: goal.steps.map((x, j) => (j === i ? { ...x, done: !x.done } : x)) })} />
                    <span>{s.text}</span>
                  </label>
                  <button type="button" className="icon-button remove" aria-label={t('Remove step {step}', { step: s.text })}
                    onClick={() => patch({ steps: goal.steps.filter((_, j) => j !== i) })}>&times;</button>
                </li>
              ))}
            </ul>
          )}
          <div className="tag-create goal-add-step">
            <input value={newStep} onChange={(e) => setNewStep(e.target.value)} maxLength={200} placeholder={t('Add a step')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addStep();
                }
              }} />
            <button type="button" className="secondary small" onClick={addStep} disabled={!newStep.trim()}>{t('Add')}</button>
          </div>
          <div className="goal-controls">
            <select value={goal.status} onChange={(e) => patch({ status: e.target.value, doneAt: e.target.value === 'done' ? tradingDayKey(new Date()) : null })} aria-label={t('Status')}>
              {Object.entries(STATUS_LABEL).map(([id, label]) => <option key={id} value={id}>{t(label)}</option>)}
            </select>
            {goal.steps.length === 0 && goal.status !== 'done' && (
              <label className="goal-slider">
                <span>{t('Progress {pct}%', { pct: goal.progress ?? 0 })}</span>
                <input type="range" min="0" max="100" step="5" value={goal.progress ?? 0}
                  onChange={(e) => onChange({ ...goal, progress: Number(e.target.value) })}
                  onMouseUp={(e) => patch({ progress: Number(e.target.value) })}
                  onTouchEnd={(e) => patch({ progress: Number(e.target.value) })}
                  onKeyUp={(e) => patch({ progress: Number(e.target.value) })} />
              </label>
            )}
          </div>
        </>
      )}
      {goal.notes && <p className="goal-notes">{goal.notes}</p>}
    </li>
  );
}

function Goals({ user, accounts, trades, notes, onSessionExpired }) {
  const { t, tr } = useI18n();
  const [vision, setVision] = useState('');
  const [goals, setGoals] = useState(null);
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null); // null, 'new' or a goal
  const [visionState, setVisionState] = useState('idle');
  const visionTimer = useRef(null);

  useEffect(() => {
    Promise.all([api('/goals'), api('/routine')])
      .then(([g, r]) => {
        setVision(g.vision);
        setGoals(g.goals);
        setRules(r.rules);
      })
      .catch((err) => {
        if (err.status === 401) onSessionExpired();
      });
  }, [onSessionExpired]);

  function changeVision(text) {
    setVision(text);
    setVisionState('pending');
    if (visionTimer.current) clearTimeout(visionTimer.current);
    visionTimer.current = setTimeout(async () => {
      try {
        await api('/goals/vision', { method: 'PUT', body: { vision: text } });
        setVisionState('saved');
      } catch (err) {
        if (err.status === 401) return onSessionExpired();
        setVisionState('error');
      }
    }, 700);
  }

  const data = { accounts, trades, notes, rules };
  const replace = (g) => setGoals((list) => list.map((x) => (x._id === g._id ? g : x)));

  // A tracked goal the trader's data shows as reached gets its day saved once, so My Trading
  // Journey can show when it happened and a later dip doesn't undo it.
  useEffect(() => {
    if (!goals || !rules) return;
    const today = tradingDayKey(new Date());
    goals.filter((g) => g.auto && !g.doneAt && (autoProgress(g, { accounts, trades, notes, rules }) || {}).done).forEach((g) => {
      api(`/goals/${g._id}`, { method: 'PATCH', body: { doneAt: today, status: 'done' } }).then(replace).catch(() => {});
    });
  }, [goals, rules, accounts, trades, notes]); // eslint-disable-line react-hooks/exhaustive-deps

  function section(horizon, title, hint) {
    const list = (goals || []).filter((g) => g.horizon === horizon);
    // Unfinished first, then by target date.
    const sorted = [...list].sort((a, b) => {
      const da = goalProgress(a, data).done;
      const db = goalProgress(b, data).done;
      if (da !== db) return da ? 1 : -1;
      return (a.targetDate || '9999').localeCompare(b.targetDate || '9999');
    });
    return (
      <section className="goal-section">
        <div className="goal-section-head">
          <div>
            <h2>{title}</h2>
            <p className="admin-panel-sub">{hint}</p>
          </div>
        </div>
        {sorted.length ? (
          <ul className="goal-list">
            {sorted.map((g) => (
              <GoalCard key={g._id} goal={g} data={data} onChange={replace} onEdit={() => setEditing(g)}
                onSessionExpired={onSessionExpired} />
            ))}
          </ul>
        ) : (
          <p className="empty">{horizon === 'short' ? t('No short-term goals yet.') : t('No long-term goals yet.')}</p>
        )}
      </section>
    );
  }

  return (
    <div className="goals-page">
      <div className="routine-header">
        <div>
          <p className="eyebrow">{t('Goals')}</p>
          <h1 className="routine-title">{t("Where you're going")}</h1>
          <p className="subtitle">{t("Your vision, the results you're after, and the habits that will get you there.")}</p>
        </div>
        <button type="button" className="page-action" onClick={() => setEditing('new')}>{t('+ New goal')}</button>
      </div>

      <section className="vision-card">
        <div className="routine-block-head">
          <span className="vision-label">{t('My trading vision')}</span>
          <span className={`save-status ${visionState === 'saved' ? 'success' : visionState === 'error' ? 'error' : ''}`}>
            {visionState === 'pending' ? t('Saving…') : visionState === 'saved' ? t('Saved') : visionState === 'error' ? t('Could not save') : ''}
          </span>
        </div>
        <textarea className="vision-text" rows="3" value={vision} maxLength={2000} onChange={(e) => changeVision(e.target.value)}
          placeholder={t('Why do you trade, and what does success look like? e.g. Trade full-time from home within 2 years, funded with $300K across 3 accounts, so I can be there for my family.')} />
      </section>

      {goals === null ? (
        <p className="empty">{t('Loading…')}</p>
      ) : (
        <div className="goal-sections">
          {section('short', t('Short-term goals'), t('This week or this month'))}
          {section('long', t('Long-term goals'), t('6 months and beyond'))}
        </div>
      )}

      <p className="goal-tip">
        {tr("<0>Tip:</0> balance <1>trading goals</1> (results like passing an evaluation) with <2>process goals</2> (habits like following your rules), and keep a <3>personal goal</3> or two for life outside the charts. You can't control results day to day, but you can control your process.",
          [(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>])}
      </p>

      {editing && (
        <GoalEditor
          goal={editing === 'new' ? null : editing}
          user={user}
          trades={trades}
          accounts={accounts.filter((a) => !a.archived || (editing !== 'new' && editing.auto && editing.auto.accountId === a._id))}
          onSaved={(g) => {
            setGoals((list) => (list.some((x) => x._id === g._id) ? list.map((x) => (x._id === g._id ? g : x)) : [...list, g]));
            setEditing(null);
          }}
          onDeleted={(id) => {
            setGoals((list) => list.filter((x) => x._id !== id));
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
          onSessionExpired={onSessionExpired}
        />
      )}
    </div>
  );
}

export default Goals;
